/* tanstack table */
import { Table, TableState } from '@tanstack/react-table';

/* React */
import { useEffect } from 'react';

/**
 * A custom hook that updates the table state based on URL parameters.
 *
 * @template TData The type of data in the table.
 * @param {Object} props - The props object.
 * @param {boolean} [props.ignoreSearchParams] - Whether to ignore URL parameters.
 * @param {Table<TData>} [props.table] - The table instance from @tanstack/react-table.
 * @param {Partial<TableState>} [props.state] - object containing columnVisibility, columnOrder, columnFilters, and/or sorting.
 */
export const useUpdateTableState = <TData>({
    ignoreSearchParams,
    state = {},
    table
}: {
    ignoreSearchParams?: boolean;
    state: Partial<TableState>;
    table: Table<TData>;
}): void => {
    // Fetch the initial state from URL parameters
    const { columnVisibility, columnOrder, columnFilters, sorting } = state;

    useEffect(() => {
        if (ignoreSearchParams) {
            return;
        }

        // Update column visibility if present in URL
        if (columnVisibility) {
            table.setColumnVisibility(columnVisibility);
        }

        // Update column order if present in URL
        if (columnOrder) {
            table.setColumnOrder(columnOrder);
        }

        // Update column filters if present in URL
        if (columnFilters) {
            table.setColumnFilters([...columnFilters]);
        }

        // Update column sorting if present in URL
        if (sorting) {
            table.setSorting(sorting);
        }
    }, [
        columnFilters,
        columnOrder,
        columnVisibility,
        ignoreSearchParams,
        sorting,
        table
    ]);
};
